import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Alert, Box, Container, Typography } from '@mui/material';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import LoadingScreen from 'src/components/LoadingScreen';
import type { Proposal } from 'src/services/DataVaultTransfer.service';
import { fetchContinueCertification } from 'src/services/CertificationContinue.service';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import { useTransferState } from './hooks/useTransferState';
import { useSaveDecision } from './hooks/useSaveDecision';
import { useCommitTransfer } from './hooks/useCommitTransfer';
import { decisionKey } from './types';
import TransferSectionCard from './components/TransferSectionCard';
import TransferScalarProposal from './components/TransferScalarProposal';
import TransferMultiGroupProposal from './components/TransferMultiGroupProposal';
import TransferFileProposal from './components/TransferFileProposal';
import TransferFooter from './components/TransferFooter';
import OnBehalfOfBanner from 'src/components/NewRenterInterface/shared/OnBehalfOfBanner';
import { useOnBehalfOf } from 'src/hooks/useOnBehalfOf';

/**
 * Data Vault Transfer page — renter reviews pre-populated answers
 * and decides what to bring into their new certification (ENG-2264).
 *
 * memberId is read from the URL params (either the plain data-transfer route
 * for self-mode, or the members/:memberId/data-transfer route for proxy mode).
 * It is intentionally NOT read from state.interview.onBehalfOf: that field is
 * reset when InterviewRoute unmounts during the transfer-guard redirect, making
 * it unreliable here. Using a stale Redux value could route a helper into the
 * wrong member's interview context.
 */
const DataVaultTransferPage: FC = () => {
  const { surveyId, certificationId, memberId } = useParams<{
    surveyId: string;
    certificationId: string;
    memberId?: string;
  }>();
  const navigate = useNavigate();
  const sId = surveyId ? Number(surveyId) : undefined;
  const certId = certificationId ? Number(certificationId) : undefined;
  // Use the URL-param memberId (HouseholdMember.id) for proxy routing.
  const mId = memberId ? Number(memberId) : null;

  const { subjectName: onBehalfOfName } = useOnBehalfOf({ subjectUserId: mId });

  const [isContinuing, setIsContinuing] = useState(false);

  /**
   * Called from useTransferState when the transfer is already committed on
   * page load. Calls fetchContinueCertification so the resolver always picks
   * between staying on the transfer page (edge case) or routing to the IPE.
   * Preserves the /members/:memberId segment for proxy (on-behalf-of) actors
   * using the URL-param mId, not Redux state.
   */
  const handleContinue = useCallback(async (): Promise<void> => {
    if (!sId) {
      return;
    }
    setIsContinuing(true);
    try {
      const { redirect_to: redirectTo } = await fetchContinueCertification(sId);

      if (redirectTo === 'data-transfer') {
        // Already on the transfer page — just reload state (edge case).
        window.location.reload();
      } else {
        // questionnaire — go back to InterviewRoute which initialises the IPE.
        // Preserve the /members/:memberId segment for on-behalf-of helpers.
        const interviewPath = mId
          ? `/${RENTER_PATHS.HOME}/interview/${sId}/members/${mId}`
          : `/${RENTER_PATHS.HOME}/interview/${sId}`;
        navigate(interviewPath, { replace: true });
      }
    } finally {
      setIsContinuing(false);
    }
  }, [sId, mId, navigate]);

  const { state, updateDecision, removeDecision } = useTransferState(sId, {
    onAlreadyDone: handleContinue,
  });

  // Surfaces a failed decision save AND rolls the row back to the last state
  // the server actually holds. Without the rollback the screen keeps showing a
  // document as discarded after the write failed, and the renter's next
  // Continue would carry over the decision the server still has.
  const [saveError, setSaveError] = useState<string | null>(null);
  const [rollbackNonce, setRollbackNonce] = useState(0);
  const handleDecisionFailed = useCallback(() => {
    setSaveError(
      'Could not save your last change. Please set it again before you continue.'
    );
    setRollbackNonce((nonce) => nonce + 1);
  }, []);

  const { save } = useSaveDecision({
    transferId: state.transferId,
    onDecisionSaved: updateDecision,
    onDecisionRemoved: removeDecision,
    onDecisionFailed: handleDecisionFailed,
  });
  const { startCommit } = useCommitTransfer(state.transferId, sId, certId, mId);

  // isCommitting: optimistic disable of the commit button from click until
  // navigation fires (usually < 1 s). Kept local — no global state needed.
  const [isCommitting, setIsCommitting] = useState(false);
  // Surfaces a failed commit inline. Staying on the page is correct: the
  // transfer is still pending, so navigating would bounce off the route guard
  // and look like the button did nothing.
  const [commitError, setCommitError] = useState<string | null>(null);

  const handleCommit = useCallback(async (): Promise<void> => {
    setIsCommitting(true);
    setCommitError(null);
    const movedOn = await startCommit();
    if (!movedOn) {
      setCommitError('Could not continue. Please try again.');
      // Re-enable the button so the renter can retry.
      setIsCommitting(false);
    }
  }, [startCommit]);

  // Group proposals by section
  const sections = useMemo(() => {
    const sectionMap = new Map<
      number,
      { name: string; order: number; proposals: Proposal[] }
    >();
    for (const p of state.proposals) {
      if (!sectionMap.has(p.section_id)) {
        sectionMap.set(p.section_id, {
          name: p.section_name || `Section ${p.section_id}`,
          order: p.section_order,
          proposals: [],
        });
      }
      sectionMap.get(p.section_id)!.proposals.push(p);
    }
    return Array.from(sectionMap.values()).sort((a, b) => a.order - b.order);
  }, [state.proposals]);

  const renderProposal = (proposal: Proposal): ReactNode => {
    const key = `${proposal.qit_id}_${proposal.instance_index}`;
    const decision = state.decisions.get(
      decisionKey(proposal.qit_id, proposal.instance_index)
    );

    if (proposal.kind === 'file') {
      return (
        <TransferFileProposal
          key={key}
          proposal={proposal}
          decision={decision}
          onSave={save}
          rollbackNonce={rollbackNonce}
        />
      );
    }

    // Scalar (single-group or no group)
    return (
      <TransferScalarProposal
        key={key}
        proposal={proposal}
        decision={decision}
        onSave={save}
      />
    );
  };

  const renderSectionContent = (proposals: Proposal[]): ReactNode => {
    // Separate multi-group proposals from scalars/files.
    // Filter out scalar duplicates for file-type questions — the API sends both
    // a scalar (always empty) and a file proposal; we only need the file version.
    const fileQitIds = new Set(
      proposals
        .filter((p) => p.kind === 'file')
        .map((p) => `${p.qit_id}_${p.instance_index}`)
    );

    const multiGroups = new Map<string, Proposal[]>();
    const singles: Proposal[] = [];

    for (const p of proposals) {
      // Skip scalar entries that duplicate a file proposal for the same question
      if (
        p.kind === 'scalar' &&
        p.question_type === 'file' &&
        fileQitIds.has(`${p.qit_id}_${p.instance_index}`)
      ) {
        continue;
      }
      if (p.group_type === 'multi' && p.group_id) {
        const groupKey = `${p.group_id}_${p.instance_index}`;
        if (!multiGroups.has(groupKey)) {
          multiGroups.set(groupKey, []);
        }
        multiGroups.get(groupKey)!.push(p);
      } else {
        singles.push(p);
      }
    }

    return (
      <>
        {/* Render single/scalar proposals */}
        {singles.map(renderProposal)}

        {/* Render multi-group instance blocks */}
        {Array.from(multiGroups.entries()).map(([groupKey, groupProposals]) => {
          const first = groupProposals[0];
          return (
            <TransferMultiGroupProposal
              key={groupKey}
              groupLabel={first.group_label || 'Group'}
              instanceIndex={first.instance_index}
              proposals={groupProposals}
              decisions={state.decisions}
              onSave={save}
            />
          );
        })}
      </>
    );
  };

  // Loading state
  if (state.status === 'loading') {
    return (
      <>
        <OnBehalfOfBanner name={onBehalfOfName} />
        <LoadingScreen />
      </>
    );
  }

  // Error state
  if (state.status === 'error') {
    return (
      <>
        <OnBehalfOfBanner name={onBehalfOfName} />
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Alert severity="error">
            {state.error || 'Something went wrong.'}
          </Alert>
        </Container>
      </>
    );
  }

  // Main transfer page
  return (
    <>
      <OnBehalfOfBanner name={onBehalfOfName} />
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          pb: 10,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            px: 2,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            ...fadeStyle,
          }}
        >
          <Container maxWidth="md" disableGutters>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              We found answers from before
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review answers from your previous interview. Confirm, choose
              between options, or discard.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your choices are saved as you make them, and you can change them
              until you continue. If you remove an answer or a document, we will
              ask for it again in your interview. That is also where you upload
              new documents.
            </Typography>
          </Container>
        </Box>

        {/* Save failure — the row flipped locally but the server kept the old
            decision, so the renter must not continue believing it was saved. */}
        {saveError && (
          <Container maxWidth="md" disableGutters sx={{ pt: 2 }}>
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          </Container>
        )}

        {/* Accordion sections */}
        <Container maxWidth="md" disableGutters sx={fadeStyle}>
          {sections.map((section) => {
            const sectionDecidedCount = section.proposals.filter((p) =>
              state.decisions.has(decisionKey(p.qit_id, p.instance_index))
            ).length;

            return (
              <TransferSectionCard
                key={section.order}
                sectionName={section.name}
                decidedCount={sectionDecidedCount}
                totalCount={section.proposals.length}
              >
                {renderSectionContent(section.proposals)}
              </TransferSectionCard>
            );
          })}
        </Container>

        {/* Commit failure — shown next to the button that failed, so the
            renter can retry instead of staring at a screen that seems inert. */}
        {commitError && (
          <Container maxWidth="md" disableGutters sx={{ pb: 1 }}>
            <Alert severity="error">{commitError}</Alert>
          </Container>
        )}

        {/* Sticky footer */}
        <TransferFooter
          summary={state.summary}
          onCommit={handleCommit}
          isCommitting={isCommitting}
        />
      </Box>
    </>
  );
};

export default DataVaultTransferPage;
